import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Popover } from '../../../../../../../common/components/Popover/Popover';
import { Heading } from '../../../../../../../common/components/Typography/Heading/Heading';
import { Paragraph } from '../../../../../../../common/components/Typography/Paragraph/Paragraph';
import { PopUp } from '../../../../../../../common/components/popups/PopUp/PopUp';
import { useMobile } from '../../../../../../../common/services/screenSize/ScreenSizeService';
import { textEllipsis } from '../../../../../../../common/styles/Layout';
import {
  FontWeight,
  STANDARD_SHRINK,
  STANDARD_TRANSITION,
} from '../../../../../../../common/styles/constants';
import { onMobile } from '../../../../../../../common/styles/onMobile';
import { suppressEvents } from '../../../../../../../common/util/suppressEvents';

export interface Props {
  className?: string;
  label: ReactNode;
  description?: string | ReactNode;
  showDescription?: boolean;
}

export const Badge = ({ label, description, className, showDescription = true }: Props) => {
  const [showing, setShowing] = useState(false);

  const mobile = useMobile();

  const handleClick = () => {
    setShowing(true);
  };

  const handleClose = () => {
    setShowing(false);
  };

  const badge = (
    <BadgeView onClick={mobile ? handleClick : undefined} className={className}>
      {label}
    </BadgeView>
  );

  if (!showDescription) {
    return <StaticBadge className={className}>{label}</StaticBadge>;
  }

  if (mobile) {
    return (
      // There is no need for a key event for a click event that suppresses events.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div onClick={suppressEvents}>
        {badge}
        <PopUp showing={showing} onClose={handleClose}>
          <Heading h3>{label}</Heading>
          <Paragraph>{description}</Paragraph>
        </PopUp>
      </div>
    );
  }

  return <Popover content={description}>{badge}</Popover>;
};

const StaticBadge = styled.div`
  ${STANDARD_TRANSITION};
  ${textEllipsis};
  border-radius: 2px;
  display: inline-block;
  font-size: 10px;
  font-weight: ${FontWeight.BOLD};
  line-height: 1em;
  margin: 0 0 0 4px;
  padding: 3px 6px;
  text-align: center;
  text-transform: uppercase;
  user-select: none;

  ${onMobile} {
    margin: 0 0 8px;
  }
`;

export const BadgeView = styled(StaticBadge)`
  cursor: pointer;

  &:hover {
    filter: brightness(${STANDARD_SHRINK});
  }
`;
